/* eslint-disable max-lines */
import { useEffect, useMemo, useRef, useState } from 'react';

import { ApolloQueryResult } from '@apollo/client';
import { first, isArray, isEmpty, isNil, merge, uniqBy } from 'lodash';
import { useDebounce, useDebouncedCallback } from 'use-debounce';

import {
  ZoomUserFragmentFragment,
  ZoomUsersListQuery,
  useZoomUserByIdsLazyQuery,
  useZoomUsersByIdsLazyQuery,
  useZoomUsersListLazyQuery,
} from 'src/generated/mloop-graphql';

import { FilterListSelectInterface } from 'src/components/FilterSelect';

import { useLastUsedZoomUserIds } from 'src/contexts/LastUsedZoomUserIdsProvider';

import { DEBOUNCE_TIMEOUT, PAGER_DEFAULT } from 'src/constants';

import { Categories, ZoomUserDataWithCategory, ZoomUserSelectProps, ZoomUserSelectValue } from './types';

// Added this variable for quick testing of pagination in case anything fails.
const PAGE_LIMIT = PAGER_DEFAULT;

const useZoomUserSelectData = <
  TData extends ZoomUserDataWithCategory,
  InputValue extends string | string[] | undefined,
>({
  value,
  onChange,
  panelInterviewerIds,
  additionalOptions,
  isAdditionalOptionId,
  shouldHidePopoverOnSelect = true,
  shouldInvokeOnChangeOnExistingUsersDataFetch = false,
}: ZoomUserSelectProps<TData, InputValue>) => {
  const isInputValueArray = isArray(value);

  const [lastUsedZoomUserIds, { add: addLastUsedZoomUserId, remove: removeLastUsedZoomUserId }] =
    useLastUsedZoomUserIds();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, DEBOUNCE_TIMEOUT);

  const [
    fetchUsers,
    {
      loading: isZoomUsersListLoading,
      error: isZoomUsersListQueryError,
      data: zoomUsersListData,
      fetchMore: fetchMoreZoomUsers,
      updateQuery: updateZoomUsersQuery,
    },
  ] = useZoomUsersListLazyQuery();

  const [
    fetchRecentlyUsedZoomUsers,
    {
      data: recentlyUsedZoomUsersData,
      loading: isRecentlyUsedZoomUsersQueryLoading,
      error: isRecentlyUsedZoomUsersQueryError,
    },
  ] = useZoomUserByIdsLazyQuery();

  const [
    fetchExistingZoomUsers,
    { data: existingZoomUsersData, loading: isExistingZoomUsersQueryLoading, error: isExistingZoomUsersQueryError },
  ] = useZoomUserByIdsLazyQuery();

  const [
    fetchPanelInterviewersZoomUsers,
    {
      data: panelInterviewersZoomUsersData,
      loading: isPanelInterviewersZoomUsersQueryLoading,
      error: isPanelInterviewersZoomUsersQueryError,
    },
  ] = useZoomUsersByIdsLazyQuery();

  const defaultSelectedUsers = useMemo(() => {
    const user = additionalOptions?.find((option) => option?.zoomUserId === value);
    return user ? [user] : undefined;
  }, [additionalOptions, value]);

  const [selectedUsers, setSelectedUsers] = useState<ZoomUserDataWithCategory[] | undefined>(defaultSelectedUsers);
  const [isExistingZoomUsersDataSet, setIsExistingZoomUsersDataSet] = useState<boolean>(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const filterListSelectInterfaceRef = useRef<FilterListSelectInterface | null>(null);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    if (value && !isExistingZoomUsersDataSet) {
      if (
        !isExistingZoomUsersQueryLoading &&
        !isNil(existingZoomUsersData) &&
        !isEmpty(existingZoomUsersData) &&
        !isExistingZoomUsersQueryError &&
        existingZoomUsersData.zoomUsersByIds
      ) {
        const existingUsers: ZoomUserFragmentFragment[] = existingZoomUsersData.zoomUsersByIds;

        setIsExistingZoomUsersDataSet(true);
        setSelectedUsers(existingUsers.length ? existingUsers : defaultSelectedUsers);

        if (shouldInvokeOnChangeOnExistingUsersDataFetch && onChange && existingUsers) {
          onChange(existingUsers as ZoomUserSelectValue<TData, InputValue>);
        }
      } else if (isNil(existingZoomUsersData) && !isExistingZoomUsersQueryLoading && !isExistingZoomUsersQueryError) {
        fetchExistingZoomUsers({ variables: { input: { zoomUserIds: isInputValueArray ? value : [value] } } });
      }
    }
  }, [
    defaultSelectedUsers,
    existingZoomUsersData,
    fetchExistingZoomUsers,
    isExistingZoomUsersDataSet,
    isExistingZoomUsersQueryError,
    isExistingZoomUsersQueryLoading,
    isInputValueArray,
    onChange,
    shouldInvokeOnChangeOnExistingUsersDataFetch,
    value,
  ]);

  const handleScrollToEnd = () => {
    if (
      !zoomUsersListData ||
      !zoomUsersListData.zoomUsers ||
      !zoomUsersListData.zoomUsers.items ||
      isZoomUsersListLoading ||
      !zoomUsersListData.zoomUsers.nextCursor ||
      !fetchMoreZoomUsers
    ) {
      return;
    }

    setFetchingMore(true);
    fetchMoreZoomUsers({
      variables: {
        input: {
          pageInput: {
            cursor: zoomUsersListData.zoomUsers.nextCursor,
            limit: PAGE_LIMIT,
          },
          prefix: '',
        },
      },
    })
      ?.then((result: ApolloQueryResult<ZoomUsersListQuery>) => {
        // eslint-disable-next-line promise/always-return
        if (!updateZoomUsersQuery) {
          return;
        }

        updateZoomUsersQuery((prevResult: ZoomUsersListQuery) => {
          const { zoomUsers } = prevResult;
          const newResults = merge({}, prevResult, result.data);

          if (newResults.zoomUsers?.items) {
            newResults.zoomUsers.items = uniqBy(
              [...(zoomUsers?.items || []), ...(result.data?.zoomUsers?.items || [])],
              'zoomUserId'
            );
          }

          return { ...newResults };
        });
      })
      .finally(() => {
        setFetchingMore(false);
      });
  };

  const debouncedHandleScrollToEnd = useDebouncedCallback(handleScrollToEnd, DEBOUNCE_TIMEOUT);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    fetchUsers({
      variables: {
        input: {
          pageInput: {
            limit: PAGE_LIMIT,
          },
          prefix: debouncedSearch,
        },
      },
    });
  }, [debouncedSearch, fetchUsers]);

  const handleAddAccount = (id: string) => {
    // The add function already handles deduplication (moves to front if exists)
    addLastUsedZoomUserId(id);
  };

  const handleRemoveRecentAccount = (zoomUserId: string) => {
    removeLastUsedZoomUserId(zoomUserId);
  };

  const users = useMemo(() => {
    let usersArray: ZoomUserDataWithCategory[] = (
      zoomUsersListData ? zoomUsersListData.zoomUsers?.items : []
    ) as ZoomUserDataWithCategory[];

    if (
      !isNil(recentlyUsedZoomUsersData) &&
      !isEmpty(recentlyUsedZoomUsersData) &&
      !isRecentlyUsedZoomUsersQueryError
    ) {
      const recentlyUsedZoomUsers = (
        recentlyUsedZoomUsersData ? recentlyUsedZoomUsersData.zoomUsersByIds : []
      ) as ZoomUserDataWithCategory[];

      usersArray = [...usersArray, ...recentlyUsedZoomUsers];
    }

    if (
      !isNil(panelInterviewersZoomUsersData) &&
      !isEmpty(panelInterviewersZoomUsersData) &&
      !isPanelInterviewersZoomUsersQueryError
    ) {
      const panelInterviewersZoomUsers = (
        panelInterviewersZoomUsersData ? panelInterviewersZoomUsersData.zoomUsersByIds : []
      ) as ZoomUserDataWithCategory[];

      usersArray = [...usersArray, ...panelInterviewersZoomUsers];
    }

    return usersArray
      .map((user) => ({ ...user, category: Categories.AllAccounts }))
      .filter((user) => !!user?.zoomUserId);
  }, [
    isPanelInterviewersZoomUsersQueryError,
    isRecentlyUsedZoomUsersQueryError,
    panelInterviewersZoomUsersData,
    recentlyUsedZoomUsersData,
    zoomUsersListData,
  ]);

  // Fetching recent interviewer details if they do not exist in existing users data.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (
      zoomUsersListData &&
      !isNil(lastUsedZoomUserIds) &&
      !isEmpty(lastUsedZoomUserIds) &&
      !isRecentlyUsedZoomUsersQueryLoading &&
      !recentlyUsedZoomUsersData
    ) {
      let recentlyUsedZoomUserIds = lastUsedZoomUserIds ? [...lastUsedZoomUserIds] : [];

      const existingZoomUserIds = users
        .filter((user) => recentlyUsedZoomUserIds.includes(user.zoomUserId))
        .map((user) => user.zoomUserId);

      if (!isEmpty(existingZoomUserIds)) {
        recentlyUsedZoomUserIds = recentlyUsedZoomUserIds.filter((userId) => !existingZoomUserIds.includes(userId));
      }

      if (!isEmpty(recentlyUsedZoomUserIds)) {
        const ids = recentlyUsedZoomUserIds
          .filter((id) => !isAdditionalOptionId || !isAdditionalOptionId(id))
          .filter((id) => !isNil(id));
        if (!ids.length) {
          return;
        }
        fetchRecentlyUsedZoomUsers({ variables: { input: { zoomUserIds: ids } } });
      }
    }
  }, [
    fetchRecentlyUsedZoomUsers,
    isRecentlyUsedZoomUsersQueryLoading,
    isAdditionalOptionId,
    lastUsedZoomUserIds,
    recentlyUsedZoomUsersData,
    users,
    zoomUsersListData,
  ]);

  // Fetching panel interviewer details if they do not exist in existing users data.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (
      zoomUsersListData &&
      !isNil(panelInterviewerIds) &&
      !isEmpty(panelInterviewerIds) &&
      !isPanelInterviewersZoomUsersQueryLoading &&
      !panelInterviewersZoomUsersData
    ) {
      let panelInterviewerIdsArray = panelInterviewerIds ? [...panelInterviewerIds] : [];

      const existingZoomUserIds = users
        .filter((user) => {
          if (user?.employeeId) {
            return panelInterviewerIdsArray.includes(user.employeeId);
          }
          return false;
        })
        .map((user) => user.employeeId);

      if (!isEmpty(existingZoomUserIds)) {
        panelInterviewerIdsArray = panelInterviewerIdsArray.filter(
          (interviewerId) => !existingZoomUserIds.includes(interviewerId)
        );
      }

      if (!isEmpty(panelInterviewerIdsArray)) {
        const ids = panelInterviewerIdsArray
          .filter((id) => !isAdditionalOptionId || !isAdditionalOptionId(id))
          .filter((id) => !isNil(id));
        if (!ids.length) {
          return;
        }
        fetchPanelInterviewersZoomUsers({ variables: { input: { employeeIds: ids } } });
      }
    }
  }, [
    fetchPanelInterviewersZoomUsers,
    isPanelInterviewersZoomUsersQueryLoading,
    isAdditionalOptionId,
    panelInterviewerIds,
    panelInterviewersZoomUsersData,
    users,
    zoomUsersListData,
  ]);

  const handleChange = (_event, allOptions: ZoomUserDataWithCategory[]) => {
    const selectedOption = first(allOptions);

    if (!selectedOption) {
      return;
    }

    if (selectedOption?.zoomUserId) {
      if (onChange) {
        onChange((isInputValueArray ? allOptions : selectedOption) as ZoomUserSelectValue<TData, InputValue>);
      }

      handleAddAccount(selectedOption?.zoomUserId);

      setSelectedUsers([...allOptions]);
    }
    if (shouldHidePopoverOnSelect) {
      filterListSelectInterfaceRef.current?.dismiss();
    }
  };

  const panelZoomUsers = useMemo(
    () =>
      panelInterviewerIds
        .map((interviewerId) => {
          for (let i = 0; i < users.length; i++) {
            if (users[i].employeeId === interviewerId && users[i].zoomUserId) {
              return {
                ...users[i],
                category: Categories.InterviewPanel,
              };
            }
          }
          return null;
        })
        .filter((user) => !!user?.zoomUserId),
    [panelInterviewerIds, users]
  );

  const recentZoomUsers = useMemo(
    () =>
      lastUsedZoomUserIds
        .map((userId) => {
          for (let i = 0; i < users.length; i++) {
            if (users[i]?.zoomUserId === userId) {
              return {
                ...users[i],
                category: Categories.RecentAccounts,
              };
            }
          }
          return null;
        })
        .filter((user) => !!user?.zoomUserId),
    [lastUsedZoomUserIds, users]
  );

  const options = uniqBy(
    [...(additionalOptions || ([] as ZoomUserDataWithCategory[])), ...panelZoomUsers, ...recentZoomUsers, ...users],
    'email'
  );

  const onInputChange = (_, inputValue: string) => {
    setSearch(inputValue);
  };

  const selectedValues = useMemo(
    () => (isInputValueArray ? selectedUsers : undefined),
    [isInputValueArray, selectedUsers]
  );

  return {
    options,
    selectedUsers,
    handleChange,
    handleRemoveRecentAccount,
    onInputChange,
    filterListSelectInterfaceRef,
    handleScrollToEnd: debouncedHandleScrollToEnd,
    isFetchingMore: fetchingMore,
    selectedValues,
    isZoomUsersListLoading,
    isZoomUsersListQueryError,
    isSearchEmpty: isEmpty(debouncedSearch),
  };
};

export default useZoomUserSelectData;
